import { useQuery, useMutation, NetworkStatus, useApolloClient } from '@apollo/client'
import format from 'date-fns/format'
import { Formik } from 'formik'
import { isEmpty, findIndex } from 'lodash'
import { ReactElement, ReactNode, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Row } from '@10xLabs/react-web/components/Row'
import { ErrorMessage } from '@10xLabs/react-web/components/ErrorMessage'
import { Column } from '@10xLabs/react-web/components/Column'
import EmptyState from '@10xLabs/react-web/components/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { InputDate } from 'components/v2/InputDate'
import FormLayout from 'features/App/components/FormLayout'
import { PlaceAutocomplete } from 'features/Places/components/PlaceAutocomplete'
import { CREATE_BOOKING_TRIP_CHANGE_PROPOSAL } from 'features/Bookings/mutations-legacy'
import { GET_SEATS_TEMPLATE } from 'features/VehicleTemplates/queries-legacy'
import { GET_TRIP_SEGMENTS } from 'features/Trips/queries-legacy'
import { mapBookingProposalToBookingQuery } from 'models/bookings'
import { DATE_FORMAT } from 'utils/date'
import { schema } from './schema'
import { TripsList } from './TripsList'
import { IMovedBooking, IMovedBookingTicket, IMovedBookingTrip } from 'features/Bookings/types'

const H5 = styled.div<TSFixMe>`
  font-size: 16px;
  font-weight: 500;
`

const getPassengerTypesByTickets = (tickets: IMovedBookingTicket[]): TSFixMe => {
  return tickets.reduce((acc: TSFixMe, ticket): TSFixMe => {
    const passengerTypeIndex = findIndex(acc, ({ id }): TSFixMe => id === ticket.passenger?.type?.id)
    if (passengerTypeIndex > -1) {
      acc[passengerTypeIndex] = {
        id: acc[passengerTypeIndex].id,
        quantity: acc[passengerTypeIndex].quantity + 1
      }
      return acc
    } else {
      return [
        ...acc,
        {
          id: ticket.passenger?.type?.id,
          quantity: 1
        }
      ]
    }
  }, [])
}

interface IEditTripProps {
  booking: IMovedBooking
  trip: IMovedBookingTrip
  oldTickets?: TSFixMe[]
  handleAbortEditTrip: () => void
  onStartToReserveSeat: () => void
  onCancelReservation: () => void
  editPriceDisabled?: boolean
  canEditSegment?: boolean
  onBookingTripChangeProposalCreated: (data: TSFixMe) => void
}

export const EditTrip = ({
  booking,
  trip,
  oldTickets = [],
  handleAbortEditTrip,
  onStartToReserveSeat,
  onCancelReservation,
  editPriceDisabled = false,
  canEditSegment,
  onBookingTripChangeProposalCreated
}: IEditTripProps): ReactElement => {
  let content
  const intl = useIntl()
  const client = useApolloClient()
  const [departureDate, setDepartureDate] = useState(new Date(trip.segment.departureAt))
  const [origin, setOrigin] = useState(trip.segment.origin)
  const [destination, setDestination] = useState(trip.segment.destination)

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_TRIP_SEGMENTS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !origin.id || !destination.id,
    variables: {
      input: {
        salesChannelID: booking.salesChannel.id,
        originID: origin.id,
        destinationID: destination.id,
        departureDate: format(departureDate, DATE_FORMAT),
        passengerTypes: getPassengerTypesByTickets(oldTickets),
        vertical: booking.vertical
      }
    }
  })

  const [
    createBookingTripChangeProposal,
    { loading: createBookingTripChangeProposalLoading, error: createBookingTripChangeProposalError }
  ] = useMutation(CREATE_BOOKING_TRIP_CHANGE_PROPOSAL)

  const handleMoveBookingTripChange = async (values: TSFixMe, { resetForm }: TSFixMe): Promise<void> => {
    try {
      const { data } = await createBookingTripChangeProposal({
        variables: {
          input: {
            id: booking.id,
            oldTrip: {
              id: trip.id,
              segmentID: trip.segment.id
            },
            newTrip: {
              id: values.newTripSegment.tripID,
              segmentID: values.newTripSegment.segment.id
            },
            seatIDs: values.tickets.map(({ seat }: TSFixMe) => seat.id),
            keepPrice: editPriceDisabled
          }
        }
      })
      const newBooking = mapBookingProposalToBookingQuery(data.createBookingTripChangeProposal, booking, trip)
      onBookingTripChangeProposalCreated({
        booking: newBooking,
        tripChangeProposalId: data.createBookingTripChangeProposal.id,
        modifiedTripId: values.newTripSegment.tripID
      })
    } catch (error) {
      resetForm({
        values: {
          ...values,
          tickets: []
        }
      })
      client.refetchQueries({
        include: [GET_SEATS_TEMPLATE]
      })
    }
  }

  const onSubmit = (values: TSFixMe, { resetForm }: TSFixMe): TSFixMe => {
    handleMoveBookingTripChange(values, { resetForm })
  }

  return (
    <Formik
      initialValues={{
        newTripSegment: {
          id: ''
        },
        tripChangeId: '',
        tickets: []
      }}
      validationSchema={schema(intl, oldTickets)}
      onSubmit={onSubmit}
    >
      {(props): TSFixMe => {
        const { handleSubmit } = props

        if (networkStatus === NetworkStatus.refetch || loading) {
          content = (
            <EmptyState.Container>
              <Loader />
            </EmptyState.Container>
          )
        } else if (error) {
          content = (
            <EmptyState.Container>
              <EmptyState.Description>
                <FormattedMessage
                  id="bookings.edit.trips.error.description"
                  defaultMessage="There was an error while loading your trips"
                />
              </EmptyState.Description>
              <EmptyState.Action>
                <Button onClick={(): TSFixMe => refetch()} kind="primary">
                  <FormattedMessage id="bookings.edit.trips.error.action" defaultMessage="Reload your trips" />
                </Button>
              </EmptyState.Action>
            </EmptyState.Container>
          )
        } else if (!data || isEmpty(data.tripSegments)) {
          content = (
            <EmptyState.Container>
              <EmptyState.Description>
                <FormattedMessage
                  id="bookings.edit.trips.empty.description"
                  defaultMessage="No trips found for this day."
                />
                <br />
                <FormattedMessage id="bookings.edit.trips.empty.action" defaultMessage="Please try a new date." />
              </EmptyState.Description>
            </EmptyState.Container>
          )
        } else {
          // @ts-expect error FIXME
          content = (
            <>
              {/*@ts-expect-error ts(2322) FIXME*/}
              <TripsList
                tripSegments={data.tripSegments}
                oldTickets={oldTickets}
                booking={booking}
                oldTrip={trip}
                onStartToReserveSeat={onStartToReserveSeat}
                onCancelReservation={onCancelReservation}
                editPriceDisabled={editPriceDisabled}
                {...props}
              />
            </>
          )
        }

        const resetTripChange = (): TSFixMe => {
          props.setFieldValue('tickets', [])
          props.setFieldValue('newTripSegment', { id: '' })
        }

        return (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <FormLayout.CardContent>
              <Row>
                <Column>
                  <H5>
                    <FormattedMessage id="bookings.edit.trips.selectDate" defaultMessage="1. Filter trips" />
                  </H5>
                </Column>
              </Row>
              <Row>
                <Column width={`${100 / 3}%`}>
                  <InputDate
                    id="departureDate"
                    intl={intl}
                    value={departureDate}
                    onChange={(value): TSFixMe => {
                      /*@ts-expect-error ts(2322) FIXME*/
                      setDepartureDate(value)
                      resetTripChange()
                    }}
                    disabledDays={{
                      before: new Date()
                    }}
                    label={<FormattedMessage id="dictionary.departureDate" defaultMessage="Departure date" />}
                  />
                </Column>
                {canEditSegment && (
                  <>
                    <Column width={`${100 / 3}%`}>
                      <PlaceAutocomplete
                        value={origin.name}
                        label={<FormattedMessage id="dictionary.origin" defaultMessage="Origin" />}
                        onChange={(place: TSFixMe): TSFixMe => {
                          setOrigin(place)
                          resetTripChange()
                        }}
                        vertical={booking.vertical}
                      />
                    </Column>
                    <Column width={`${100 / 3}%`}>
                      <PlaceAutocomplete
                        value={destination.name}
                        label={<FormattedMessage id="dictionary.destination" defaultMessage="Destination" />}
                        onChange={(place: TSFixMe): TSFixMe => {
                          setDestination(place)
                          resetTripChange()
                        }}
                        vertical={booking.vertical}
                      />
                    </Column>
                  </>
                )}
              </Row>
              <Row>
                <Column>
                  <H5>
                    <FormattedMessage
                      id="bookings.edit.trips.selectTrip"
                      defaultMessage="2. Select a new trip and seats"
                    />
                  </H5>
                </Column>
              </Row>
              <Row>
                <Column>{content}</Column>
              </Row>
            </FormLayout.CardContent>
            <FormLayout.Split />
            <FormLayout.CardFooter>
              {props.touched.newTripSegment &&
                props.touched.newTripSegment.id &&
                props.errors.newTripSegment &&
                props.errors.newTripSegment.id && (
                  <div>
                    <ErrorMessage>{props.errors.newTripSegment.id}</ErrorMessage>
                  </div>
                )}
              {props.touched.tickets && props.errors.tickets && (
                <div>
                  <ErrorMessage>{props.errors.tickets as ReactNode}</ErrorMessage>
                </div>
              )}
              {createBookingTripChangeProposalError && (
                <div>
                  <ErrorMessage>
                    <FormattedMessage
                      id="bookings.edit.trips.createBookingProposalError.description"
                      defaultMessage="Hubo un error al guardar tus cambios. Intenta de nuevo."
                    />
                  </ErrorMessage>
                </div>
              )}
              <FormLayout.CardFooterActions>
                <Button
                  type="button"
                  onClick={(): TSFixMe => {
                    if (props.values.tripChangeId) {
                      props.setFieldValue('tickets', [])
                      onCancelReservation()
                    }
                    handleAbortEditTrip()
                  }}
                  kind="cancel"
                >
                  <FormattedMessage id="dictionary.back" defaultMessage="Back" />
                </Button>
                <Button
                  type="submit"
                  kind="primary"
                  loading={createBookingTripChangeProposalLoading}
                  disabled={loading || !!error}
                >
                  <FormattedMessage id="dictionary.save" defaultMessage="Save" />
                </Button>
              </FormLayout.CardFooterActions>
            </FormLayout.CardFooter>
          </form>
        )
      }}
    </Formik>
  )
}
